import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { LOGO, CITY_URL, scrollToId } from "@/constants/assets";

const LINKS = [
  { label: "CITY", id: "city" },
  { label: "LIFE", id: "life" },
  { label: "HOW IT RUNS", id: "systems" },
  { label: "EXPERIMENTS", id: "experiments" },
  { label: "ABOUT", id: "about" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-[100] transition-all duration-500 ${
        scrolled
          ? "translate-y-0 opacity-100 bg-gb-bg/85 backdrop-blur-md border-b border-gb-line"
          : "-translate-y-full opacity-0 bg-transparent border-b border-transparent"
      }`}
    >
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
          {LINKS.map((l) => (
            <a
              key={l.id}
              data-testid={`nav-link-${l.id.toLowerCase()}`}
              href={`#${l.id}`}
              onClick={(e) => {
                e.preventDefault();
                go(l.id);
              }}
              className="text-[10px] tracking-[0.18em] text-gb-accent hover:text-gb-neon transition-colors whitespace-nowrap"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <button
          data-testid="nav-logo-home"
          onClick={() => window.__lenis ? window.__lenis.scrollTo(0) : window.scrollTo({top:0, behavior:"smooth"})}
          className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2"
        >
          <img src={LOGO} alt="replicant" className="h-8 sm:h-9 w-auto" />
        </button>

        <div className="flex items-center gap-3 ml-auto">
          <a
            data-testid="nav-live-cta"
            href={CITY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex items-center gap-2 border-2 border-gb-accent/60 px-4 py-2 text-[11px] tracking-[0.2em] text-gb-pale hover:bg-gb-neon hover:text-gb-bg hover:border-gb-neon transition-all duration-200"
          >
            <span className="w-1.5 h-1.5 bg-gb-neon animate-blink" />
            LIVE CITY
          </a>
          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden text-gb-accent p-1"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-gb-bg/95 backdrop-blur-md border-b border-gb-line px-6 py-5 flex flex-col gap-4">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-mobile-link-${l.id.toLowerCase()}`}
              onClick={() => go(l.id)}
              className="text-left text-xs tracking-[0.25em] text-gb-mint"
            >
              {l.label}
            </button>
          ))}
          <a
            data-testid="nav-mobile-live-cta"
            href={CITY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 inline-flex items-center gap-2 border-2 border-gb-accent px-4 py-2.5 text-[11px] tracking-[0.2em] text-gb-pale w-max"
          >
            <span className="w-1.5 h-1.5 bg-gb-neon animate-blink" />
            LIVE CITY
          </a>
        </div>
      )}
    </header>
  );
}
